import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Download, Send, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import JourneyStepSelector from './JourneyStepSelector';
import magnetService from '@/services/api/magnetService';

interface Magnet {
  id: string;
  title: string;
  description: string;
  stage: 'STAGE_1' | 'STAGE_2' | 'STAGE_3' | 'STAGE_4' | 'STAGE_5';
  type: 'WHITEPAPER' | 'CASE_STUDY' | 'EBOOK' | 'WEBINAR' | 'GUIDE' | 'CHECKLIST';
  format: 'PDF' | 'VIDEO' | 'SLIDE_DECK' | 'INTERACTIVE';
  journeySteps: number[];
  downloads: number;
  deliveries: number;
  createdDate: string;
  author: string;
  pdfFile?: string;
  pdfFileName?: string;
  pptFile?: string;
  pptFileName?: string;
  imageFile?: string;
  imageFileName?: string;
  videoLink?: string;
}

// Journey step metadata
const journeyStepInfo = [
  { step: 1, name: 'Account & Contact Context Setup', phase: 'Discovery' },
  { step: 2, name: 'Maturity Mirror Exposure', phase: 'Discovery' },
  { step: 3, name: 'Role-Based Pain Framing', phase: 'Discovery' },
  { step: 4, name: 'Maturity Gap Awareness', phase: 'Discovery' },
  { step: 5, name: 'MES Reality Alignment', phase: 'Discovery' },
  { step: 6, name: 'Alignment Call #1', phase: 'Advisory' },
  { step: 7, name: 'Architecture Thinking Introduction', phase: 'Advisory' },
  { step: 8, name: 'Multi-System Coexistence Education', phase: 'Advisory' },
  { step: 9, name: 'Data Readiness Awareness', phase: 'Education' },
  { step: 10, name: 'Automation Timing Guidance', phase: 'Education' },
  { step: 11, name: 'Decision Intelligence Framing', phase: 'Education' },
  { step: 12, name: 'Context Confirmation Call #2', phase: 'Validation' },
  { step: 13, name: 'Future-State Snapshot', phase: 'Validation' },
  { step: 14, name: 'MQL Validation & Sales Handoff', phase: 'Validation' },
];

// Premium colors
const stageColors = {
  STAGE_1: 'tier1',
  STAGE_2: 'tier2',
  STAGE_3: 'active',
  STAGE_4: 'mqlSubmitted',
  STAGE_5: 'mqlReady',
} as const;

const typeColors = {
  WHITEPAPER: 'tier3',
  CASE_STUDY: 'mqlReady',
  EBOOK: 'tier2',
  WEBINAR: 'active',
  GUIDE: 'tier1',
  CHECKLIST: 'warning',
} as const;

function MagnetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [magnet, setMagnet] = useState<Magnet | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStage, setEditStage] = useState<Magnet['stage']>('STAGE_1');
  const [editType, setEditType] = useState<Magnet['type']>('GUIDE');
  const [editFormat, setEditFormat] = useState<Magnet['format']>('PDF');
  const [editJourneySteps, setEditJourneySteps] = useState<number[]>([]);
  const [editAuthor, setEditAuthor] = useState('');

  // File attachment states
  const [editPdfFile, setEditPdfFile] = useState<string>('');
  const [editPdfFileName, setEditPdfFileName] = useState<string>('');
  const [editPptFile, setEditPptFile] = useState<string>('');
  const [editPptFileName, setEditPptFileName] = useState<string>('');
  const [editImageFile, setEditImageFile] = useState<string>('');
  const [editImageFileName, setEditImageFileName] = useState<string>('');
  const [editVideoLink, setEditVideoLink] = useState<string>('');

  // Load magnet data (in real app, fetch from API)
  useEffect(() => {
    // Handle "new" magnet creation - FIXED: No longer tries to fetch ID "new"
    if (id === 'new') {
      const newMagnet: Magnet = {
        id: 'new',
        title: 'New Magnet',
        description: '',
        stage: 'STAGE_1',
        type: 'GUIDE',
        format: 'PDF',
        journeySteps: [],
        downloads: 0,
        deliveries: 0,
        createdDate: new Date().toISOString(),
        author: 'Unknown',
      };
      setMagnet(newMagnet);
      loadEditForm(newMagnet);
      setIsEditMode(true); // Automatically enter edit mode for new magnets
      return;
    }

    // Fetch from API
    magnetService.getMagnetById(id).then((data: any) => {
      const m: Magnet = {
        id: data.id,
        title: data.title,
        description: data.description || '',
        stage: data.stage || 'STAGE_1',
        type: (data.type?.toUpperCase() || 'GUIDE') as Magnet['type'],
        format: data.format || 'PDF',
        journeySteps: (data.applicableStages || []).map((s: string) => parseInt(s)).filter((n: number) => !isNaN(n)),
        downloads: data.downloads || 0,
        deliveries: data.deliveries || 0,
        createdDate: data.createdAt || new Date().toISOString(),
        author: data.author || '',
        pdfFileName: data.pdfFileName || '',
        pptFileName: data.pptFileName || '',
        imageFileName: data.imageFileName || '',
        videoLink: data.videoLink || '',
      };
      setMagnet(m);
      loadEditForm(m);
    }).catch(() => {
      addToast({ type: 'error', title: 'Magnet Not Found', description: 'Could not load magnet.' });
      navigate('/magnets');
    });
  }, [id, navigate, addToast]);

  const loadEditForm = (magnet: Magnet) => {
    setEditTitle(magnet.title);
    setEditDescription(magnet.description);
    setEditStage(magnet.stage);
    setEditType(magnet.type);
    setEditFormat(magnet.format);
    setEditJourneySteps(magnet.journeySteps);
    setEditAuthor(magnet.author);
    // Load file attachments
    setEditPdfFile(magnet.pdfFile || '');
    setEditPdfFileName(magnet.pdfFileName || '');
    setEditPptFile(magnet.pptFile || '');
    setEditPptFileName(magnet.pptFileName || '');
    setEditImageFile(magnet.imageFile || '');
    setEditImageFileName(magnet.imageFileName || '');
    setEditVideoLink(magnet.videoLink || '');
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (file: string) => void,
    setFileName: (name: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFile(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (
    setFile: (file: string) => void,
    setFileName: (name: string) => void
  ) => {
    setFile('');
    setFileName('');
  };

  const handleSaveEdit = () => {
    if (!magnet) return;

    // Generate new ID if creating a new magnet
    const isNewMagnet = magnet.id === 'new';
    const magnetId = isNewMagnet ? `magnet-${Date.now()}` : magnet.id;

    const updatedMagnet: Magnet = {
      ...magnet,
      id: magnetId,
      title: editTitle,
      description: editDescription,
      stage: editStage,
      type: editType,
      format: editFormat,
      journeySteps: editJourneySteps,
      author: editAuthor,
      // Include file attachments
      pdfFile: editPdfFile,
      pdfFileName: editPdfFileName,
      pptFile: editPptFile,
      pptFileName: editPptFileName,
      imageFile: editImageFile,
      imageFileName: editImageFileName,
      videoLink: editVideoLink,
    };

    const payload = {
      title: editTitle,
      description: editDescription,
      stage: editStage,
      type: editType,
      format: editFormat,
      journeySteps: editJourneySteps,
      author: editAuthor,
      videoLink: editVideoLink,
      pdfFileName: editPdfFileName,
      pptFileName: editPptFileName,
      imageFileName: editImageFileName,
    };

    const apiCall = isNewMagnet
      ? magnetService.createMagnet(payload)
      : magnetService.updateMagnet(magnet.id, payload);

    apiCall.then(() => {
      addToast({
        type: 'success',
        title: isNewMagnet ? 'Magnet Created' : 'Magnet Updated',
        description: isNewMagnet
          ? 'New magnet has been added to the library successfully.'
          : 'Your changes have been saved successfully.',
      });
      navigate('/magnets');
    }).catch((error: any) => {
      addToast({
        type: 'error',
        title: 'Error Saving Magnet',
        description: error.message || 'Failed to save magnet.',
      });
    });
  };

  const handleCancelEdit = () => {
    if (magnet) {
      loadEditForm(magnet);
    }
    setIsEditMode(false);
  };

  const handleDelete = () => {
    if (!magnet) return;
    magnetService.deleteMagnet(magnet.id).then(() => {
      addToast({ type: 'success', title: 'Magnet Deleted', description: `${magnet.title} has been removed from the library.` });
      navigate('/magnets');
    }).catch((error: any) => {
      addToast({ type: 'error', title: 'Error Deleting Magnet', description: error.message || 'Failed to delete magnet.' });
    });
  };

  const getJourneyStepName = (stepNumber: number) => {
    const step = journeyStepInfo.find(s => s.step === stepNumber);
    return step ? step.name : `Step ${stepNumber}`;
  };

  const getJourneyStepPhase = (stepNumber: number) => {
    const step = journeyStepInfo.find(s => s.step === stepNumber);
    return step ? step.phase : '';
  };

  if (!magnet) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Loading magnet details...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        onBack={() => navigate('/magnets')}
        title={isEditMode ? (magnet.id === 'new' ? 'New Magnet' : 'Edit Magnet') : magnet.title}
        actions={
          isEditMode ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditMode(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => alert(`Downloading ${magnet.title}`)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button
                variant="outline"
                onClick={() => alert(`Sending ${magnet.title}`)}
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          )
        }
      />
      {!isEditMode && (
        <div className="flex flex-wrap gap-2 -mt-2">
          <Badge variant={stageColors[magnet.stage]}>
            {magnet.stage.replace('_', ' ')}
          </Badge>
          <Badge variant={typeColors[magnet.type]}>
            {magnet.type.replace('_', ' ')}
          </Badge>
          <Badge variant="outline">{magnet.format.replace('_', ' ')}</Badge>
        </div>
      )}

      {/* Content */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Description Card */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              ) : (
                <p className="text-muted-foreground">{magnet.description}</p>
              )}
            </CardContent>
          </Card>

          {/* Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-sm font-medium mb-1 block">Title</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Type</label>
                    <select
                      value={editType}
                      onChange={(e) => setEditType(e.target.value as Magnet['type'])}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="GUIDE">Guide</option>
                      <option value="WHITEPAPER">Whitepaper</option>
                      <option value="CASE_STUDY">Case Study</option>
                      <option value="EBOOK">eBook</option>
                      <option value="WEBINAR">Webinar</option>
                      <option value="CHECKLIST">Checklist</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Format</label>
                    <select
                      value={editFormat}
                      onChange={(e) => setEditFormat(e.target.value as Magnet['format'])}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="PDF">PDF</option>
                      <option value="VIDEO">Video</option>
                      <option value="SLIDE_DECK">Slide Deck</option>
                      <option value="INTERACTIVE">Interactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Stage</label>
                    <select
                      value={editStage}
                      onChange={(e) => setEditStage(e.target.value as Magnet['stage'])}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="STAGE_1">Stage 1</option>
                      <option value="STAGE_2">Stage 2</option>
                      <option value="STAGE_3">Stage 3</option>
                      <option value="STAGE_4">Stage 4</option>
                      <option value="STAGE_5">Stage 5</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Author</label>
                    <input
                      type="text"
                      value={editAuthor}
                      onChange={(e) => setEditAuthor(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Type
                    </label>
                    <p className="text-base font-medium mt-1">{magnet.type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Format
                    </label>
                    <p className="text-base font-medium mt-1">{magnet.format.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Stage
                    </label>
                    <p className="text-base font-medium mt-1">{magnet.stage.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Author
                    </label>
                    <p className="text-base font-medium mt-1">{magnet.author}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Created
                    </label>
                    <p className="text-base font-medium mt-1">
                      {new Date(magnet.createdDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Journey Steps Card */}
          <Card>
            <CardHeader>
              <CardTitle>Journey Steps ({isEditMode ? editJourneySteps.length : magnet.journeySteps.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <JourneyStepSelector
                  selectedSteps={editJourneySteps}
                  onChange={setEditJourneySteps}
                  journeySteps={journeyStepInfo}
                />
              ) : (
                <div className="space-y-2">
                  {magnet.journeySteps.sort((a, b) => a - b).map(stepNum => (
                    <div
                      key={stepNum}
                      className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary font-semibold text-sm flex-shrink-0">
                        {stepNum}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{getJourneyStepName(stepNum)}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {getJourneyStepPhase(stepNum)} Phase
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments Card - Edit Mode */}
          {isEditMode && (
            <Card>
              <CardHeader>
                <CardTitle>Attachments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* PDF Upload */}
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center justify-between">
                    <span>PDF File</span>
                    {editPdfFileName && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveFile(setEditPdfFile, setEditPdfFileName)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, setEditPdfFile, setEditPdfFileName)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                  {editPdfFileName && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Current: {editPdfFileName}
                    </p>
                  )}
                </div>

                {/* PPT Upload */}
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center justify-between">
                    <span>PowerPoint File</span>
                    {editPptFileName && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveFile(setEditPptFile, setEditPptFileName)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </label>
                  <input
                    type="file"
                    accept=".ppt,.pptx"
                    onChange={(e) => handleFileUpload(e, setEditPptFile, setEditPptFileName)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                  {editPptFileName && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Current: {editPptFileName}
                    </p>
                  )}
                </div>

                {/* Image Upload */}
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center justify-between">
                    <span>Image/Thumbnail</span>
                    {editImageFileName && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveFile(setEditImageFile, setEditImageFileName)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setEditImageFile, setEditImageFileName)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                  {editImageFileName && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-2">
                        Current: {editImageFileName}
                      </p>
                      {editImageFile && (
                        <img
                          src={editImageFile}
                          alt="Preview"
                          className="w-full max-w-xs rounded-lg border"
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Video Link */}
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    YouTube Video Link
                  </label>
                  <input
                    type="url"
                    value={editVideoLink}
                    onChange={(e) => setEditVideoLink(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle>ABM Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">Sent to Contacts</p>
                  <p className="text-2xl font-bold mt-1">{magnet.deliveries}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total deliveries</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">MDA Usage</p>
                  <p className="text-2xl font-bold mt-1">{magnet.downloads}</p>
                  <p className="text-xs text-muted-foreground mt-1">Times used by team</p>
                </div>
                <div className="p-4 border rounded-lg col-span-2">
                  <p className="text-sm text-muted-foreground">Engagement Rate</p>
                  <p className="text-2xl font-bold mt-1">
                    {magnet.deliveries > 0
                      ? Math.round((magnet.downloads / magnet.deliveries) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Opens, clicks & revisits</p>
                </div>
              </div>
              
              {/* ABM Journey Context */}
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>ABM Flow:</strong> MDA delivers magnet → Contact receives → 
                  Engagement tracked → Stage progression measured
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Attachments Card */}
          {(magnet.pdfFile || magnet.pptFile || magnet.imageFile || magnet.videoLink) && (
            <Card>
              <CardHeader>
                <CardTitle>Attachments & Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* PDF File */}
                  {magnet.pdfFile && (
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded bg-red-100 flex items-center justify-center">
                          <Download className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{magnet.pdfFileName || 'Document.pdf'}</p>
                          <p className="text-xs text-muted-foreground">PDF Document</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = magnet.pdfFile!;
                            link.download = magnet.pdfFileName || 'document.pdf';
                            link.click();
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            alert('Share functionality coming soon!');
                          }}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* PPT File */}
                  {magnet.pptFile && (
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded bg-orange-100 flex items-center justify-center">
                          <Download className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{magnet.pptFileName || 'Presentation.pptx'}</p>
                          <p className="text-xs text-muted-foreground">PowerPoint Presentation</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = magnet.pptFile!;
                            link.download = magnet.pptFileName || 'presentation.pptx';
                            link.click();
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            alert('Share functionality coming soon!');
                          }}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Image File */}
                  {magnet.imageFile && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                            <Download className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{magnet.imageFileName || 'Image.jpg'}</p>
                            <p className="text-xs text-muted-foreground">Image File</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = magnet.imageFile!;
                              link.download = magnet.imageFileName || 'image.jpg';
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              alert('Share functionality coming soon!');
                            }}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Share
                          </Button>
                        </div>
                      </div>
                      <img
                        src={magnet.imageFile}
                        alt={magnet.imageFileName || 'Preview'}
                        className="w-full rounded-lg border"
                      />
                    </div>
                  )}

                  {/* Video Link */}
                  {magnet.videoLink && (
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded bg-red-100 flex items-center justify-center">
                          <Send className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Video Resource</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {magnet.videoLink}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(magnet.videoLink, '_blank')}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Watch
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(magnet.videoLink!);
                            alert('Link copied to clipboard!');
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete Magnet</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">{magnet.title}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MagnetDetailPage;
